import ChallengeBucket from '../model/ChallengeBucket'
import Activity from '../model/Activity'
import ActivityLog from '../model/ActivityLog'
import Challenge from '../model/Challenge'

export default class ActivityLogic {
  static getTotalProgress(activityLogs: ActivityLog[]) {
    return activityLogs.reduce((acc, log) => acc + log.amount, 0)
  }

  static activityFeedsBucket(
    activity: { bucketID?: string | null; challengeID?: string },
    bucket: ChallengeBucket,
  ): boolean {
    if (!activity.bucketID || activity.bucketID !== bucket.bucketId) return false
    if (activity.challengeID && activity.challengeID !== bucket.challengeID) return false
    return true
  }

  static getBucketProgress(
    activityLogs: ActivityLog[],
    activities: Activity[],
    bucket: ChallengeBucket,
    userID?: string,
  ): number {
    const activityIds = new Set(
      activities
        .filter((activity) => this.activityFeedsBucket(activity, bucket))
        .map((activity) => activity.id),
    )

    return activityLogs
      .filter((log) => {
        if (userID && log.userID !== userID) return false
        return activityIds.has(log.activityID)
      })
      .reduce((acc, log) => acc + log.amount, 0)
  }

  /** Legacy name-based bucket lookup. */
  static getBucketProgressByName(
    activityLogs: ActivityLog[],
    activities: Activity[],
    bucketName: string,
    buckets: ChallengeBucket[],
    userID?: string,
  ): number {
    const bucket = buckets.find((item) => item.name === bucketName)
    if (!bucket) return 0
    return this.getBucketProgress(activityLogs, activities, bucket, userID)
  }

  static getIndividualProgress(activityLogs: ActivityLog[], userID: string) {
    return activityLogs
      .filter((log) => log.userID === userID)
      .reduce((acc, log) => acc + log.amount, 0)
  }

  static hasCompletedRestDays(restDaysCount: number, restDaysTarget: number) {
    return restDaysCount >= restDaysTarget
  }

  static hasFinished(
    activityLogs: ActivityLog[],
    activities: Activity[],
    buckets: ChallengeBucket[],
    challenge: Pick<Challenge, 'goal' | 'restDaysRequired'>,
    restDaysCount: number,
  ) {
    const bucketGoalsMet = buckets.every(
      (bucket) => this.getBucketProgress(activityLogs, activities, bucket) >= bucket.goal,
    )
    const totalGoalMet = this.getTotalProgress(activityLogs) >= challenge.goal
    const restDaysMet = this.hasCompletedRestDays(restDaysCount, challenge.restDaysRequired)
    return bucketGoalsMet && totalGoalMet && restDaysMet
  }
}
